import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { reviews } from "./reviewsData";

const Reviews: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const changeReview = useCallback((newIndex: number) => {
    if (isTransitioning || !cardRef.current) return;

    const card = cardRef.current;
    const currentHeight = card.offsetHeight;

    // Lock current height
    card.style.height = `${currentHeight}px`;
    setIsTransitioning(true);

    // Brief fade, then change content and animate height together
    setTimeout(() => {
      // Change content
      setCurrentIndex(newIndex);

      // Use requestAnimationFrame to ensure content has rendered
      requestAnimationFrame(() => {
        // Measure new natural height
        card.style.height = 'auto';
        const newHeight = card.offsetHeight;

        // Set back to old height and animate to new
        card.style.height = `${currentHeight}px`;
        void card.offsetHeight;
        card.style.height = `${newHeight}px`;

        // Fade content back in
        setIsTransitioning(false);

        // Clean up explicit height after animation
        setTimeout(() => {
          card.style.height = 'auto';
        }, 300);
      });
    }, 100);
  }, [isTransitioning]);

  const goToPrevious = () => {
    changeReview(currentIndex === 0 ? reviews.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    changeReview(currentIndex === reviews.length - 1 ? 0 : currentIndex + 1);
  };

  const review = reviews[currentIndex];

  return (
    <section className="centered-section" style={{ textAlign: 'center' }}>
      <div className="translucent-box">
        <h2 className="main-heading">Customer Reviews</h2>
        <div className="carousel-container">
          <button
            className="carousel-btn carousel-btn-left carousel-btn-desktop"
            onClick={goToPrevious}
            aria-label="Previous review"
          >
            <span className="carousel-arrow carousel-arrow-left" />
          </button>
          <div ref={cardRef} className={`card carousel-card ${isTransitioning ? 'transitioning' : ''}`}>
            <span className="review-text">{review.text}</span>
            <div className="review-author">&mdash; {review.author}</div>
            {review.note && (
              <div className="review-note">
                {review.note}
              </div>
            )}
          </div>
          <button
            className="carousel-btn carousel-btn-right carousel-btn-desktop"
            onClick={goToNext}
            aria-label="Next review"
          >
            <span className="carousel-arrow carousel-arrow-right" />
          </button>
        </div>
        <div className="carousel-controls">
          <button
            className="carousel-btn carousel-btn-mobile"
            onClick={goToPrevious}
            aria-label="Previous review"
          >
            <span className="carousel-arrow carousel-arrow-left" />
          </button>
          <div className="carousel-dots">
            {reviews.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => index !== currentIndex && changeReview(index)}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
          <button
            className="carousel-btn carousel-btn-mobile"
            onClick={goToNext}
            aria-label="Next review"
          >
            <span className="carousel-arrow carousel-arrow-right" />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', marginTop: '1.5rem' }}>
          <Link to="/reviews" className="main-link">
            See all reviews
          </Link>
          <a
            href="https://g.page/r/CXB6rzJaI61ZEBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="google-review-btn"
          >
            Leave a Google Review
          </a>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
